import { RESTClient } from '@api/client';
import { API_URL } from '@api/apiDictionary';
import { replacePathParams } from 'src/utils/replacePathWithParams';
import {
  TaskExecutionExportResult,
  TaskExecutionImportInitTypeResponse,
  WorkflowExecution,
} from '@dynamika-shared/types/executions';
import axios, { AxiosRequestConfig } from 'axios';

export const runTaskExecution = async (
  taskId: string,
  clinicalTrialId: string,
  clinicalTrialInstrumentId: string,
  data: unknown
) => {
  return RESTClient.post<WorkflowExecution>(
    replacePathParams(API_URL.qcTaskExecutions, { taskId }),
    {
      clinicalTrialInstrumentId,
      data,
    },
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
    }
  );
};

/**
 * "Send to QCT" — transmits the selected series to the QCT workstation (D7-5232).
 *
 * Plain terms
 * -----------
 * The specialist picks series in the viewer and sends them to the QCT
 * workstation. The workstation later reports back, and those results are what
 * the QCT QC page pulls in with its Sync button.
 *
 * Technical
 * ---------
 * QCT has its own execution pair rather than reusing the generic
 * `POST /qc/tasks/:taskId/executions`: `qct/export` sends series out and
 * `qct/query` (see `syncQCTWorkstationReadings`) asks for results back. Both
 * record an execution of type `QCT`. The body is a `RunExecutionDto`, so
 * `clinicalTrialInstrumentId` is required and `data` carries the selection.
 */
export const runQCTExport = async (
  taskId: string,
  clinicalTrialId: string,
  clinicalTrialInstrumentId: string,
  data: unknown
) => {
  return RESTClient.post<WorkflowExecution>(
    replacePathParams(API_URL.QCTExport, { taskId }),
    {
      clinicalTrialInstrumentId,
      data,
    },
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
    }
  );
};

export const runTaskExecutionExportRun = async (
  taskId: string,
  clinicalTrialId: string,
  clinicalTrialInstrumentId: string,
  data: unknown
) => {
  return RESTClient.post<TaskExecutionExportResult>(
    replacePathParams(API_URL.qcTaskExecutionsExportRun, { taskId }),
    {
      clinicalTrialInstrumentId,
      data,
    },
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
    }
  );
};

export const runTaskExecutionImportInit = async (
  taskId: string,
  clinicalTrialId: string,
  clinicalTrialInstrumentId: string
) => {
  return RESTClient.post<TaskExecutionImportInitTypeResponse>(
    replacePathParams(API_URL.qcTaskExecutionsImportInit, { taskId }),
    {
      clinicalTrialInstrumentId,
    },
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
    }
  );
};

interface UploadOptions extends AxiosRequestConfig {
  signal?: AbortSignal;
}

export const runTaskExecutionImportSendFile = async (
  file: Blob,
  signedUrl: string,
  contentType?: string,
  options: UploadOptions = {}
) => {
  //no response
  const response = await axios.request({
    method: 'PUT',
    url: signedUrl,
    data: file,
    headers: {
      'Content-Type': contentType ?? 'application/zip',
    },
    ...options,
  });

  return { code: response.status };
};

export const runTaskExecutionImportRun = async (
  taskId: string,
  clinicalTrialId: string,
  clinicalTrialInstrumentId: string,
  executionFileId: string,
  data: unknown
) => {
  return RESTClient.post<WorkflowExecution>(
    replacePathParams(API_URL.qcTaskExecutionsImportRun, { taskId }),
    {
      clinicalTrialInstrumentId,
      executionFileId,
      data,
    },
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
    }
  );
};
export const runDICOMDownloadAudit = async (
  clinicalTrialId: string,
  taskId: string,
  data: unknown,
  subTaskId?: string
) => {
  const url = subTaskId
    ? replacePathParams(API_URL.readerTaskSubtaskDICOMDownloadAudit, {
        taskId,
        subTaskId,
      })
    : replacePathParams(API_URL.readerTaskDICOMDownloadAudit, { taskId });
  return RESTClient.post(
    url,
    data,
    {
      headers: {
        'x-dynamika-clinical-trial-id': clinicalTrialId,
      },
    }
  );
};
